<template>
    <div class="md-layout">
        <div class="md-layout-item md-medium-size-100 md-xsmall-size-100 md-size-100">
            <div class="custom-toolbar">
                <div class="custom-toolbar-start">
                    <router-link
                        :to="{
                            name: 'Service Areas'
                        }"
                    >
                        <h3 class="title">Service Areas</h3>
                    </router-link>
                    <h3 class="title delivery-window-crumb-title">
                        &nbsp;&nbsp; > &nbsp;&nbsp; {{ deliveryWindowData.serviceAreaName }} &nbsp;&nbsp; > &nbsp;&nbsp;
                        Delivery Windows
                    </h3>
                </div>
            </div>
        </div>
        <div class="md-layout-item md-size-100">
            <md-card>
                <md-card-header class="md-card-header-icon md-card-header-black">
                    <div class="card-icon">
                        <md-icon>my_location</md-icon>
                    </div>
                </md-card-header>
                <md-card-content>
                    <div class="delivery-window-page-title">
                        Delivery Windows
                    </div>
                    <tabs
                        :tab-name="tabNames"
                        :selected-panel="currentTab"
                        color-button="primary"
                        class="custom-tab-list"
                        @switch-panel="beforeTabChanged"
                    >
                        <template v-for="(tabName, index) in tabNames" :slot="`tab-pane-${index + 1}`">
                            <div :key="index">
                                <service-areas-delivery-window
                                    :service-area-id="serviceAreaId"
                                    :day-of-the-week="dayOfTheWeek"
                                    :delivery-window-data="deliveryWindowData"
                                />
                            </div>
                        </template>
                    </tabs>
                    <div class="custom-toolbar copy-from-toolbar-container">
                        <div class="custom-toolbar-end copy-from-toolbar-end-container">
                            <vue-select
                                :reduce="(x) => x.id"
                                label="name"
                                :options="copyFrom"
                                v-model="copyFromDay"
                                placeholder="Copy from"
                                @input="handleCopyFromSelectedDay"
                            ></vue-select>
                        </div>
                    </div>
                    <div class="custom-toolbar custom-delivery-window-toolbar">
                        <div class="custom-toolbar-start">
                            <md-button
                                class="md-button md-primary custom-back-button"
                                @click="handleBackToServiceAreas"
                            >
                                Back
                            </md-button>
                        </div>
                        <div class="custom-toolbar-end">
                            <md-button
                                class="md-button md-primary custom-save-button"
                                @click="() => handleSaveDeliveryWindows()"
                            >
                                Save
                            </md-button>
                        </div>
                    </div>
                </md-card-content>
            </md-card>
        </div>
    </div>
</template>

<script>
import { Tabs } from '@/components';
import { handleRequests, showErrorMessage, rowStatus } from '@/helpers';
import { GeneralMixin } from '@/mixins/GeneralMixin';
import ServiceAreasDeliveryWindow from './ServiceAreasDeliveryWindow';

const fortnightlyOccurrence = 1;

export default {
    name: 'ServiceAreasDeliveryWindowPage',
    mixins: [GeneralMixin],
    components: {
        Tabs,
        ServiceAreasDeliveryWindow
    },
    data() {
        return {
            tabNames: ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'],
            copyFrom: [],
            copyFromDay: null,
            serviceAreaId: null,
            dayOfTheWeek: null,
            currentTab: null,
            deliveryWindowData: {
                serviceAreaName: null,
                deliveryWindows: []
            },
            previousDeliveryWindows: null,
            rowStateFields: [
                'startTime',
                'endTime',
                'maxStops',
                'maxVolume',
                'maxWeight',
                'maxQuantity',
                'occurrence',
                'occurrenceStartDate',
                'cutOffType',
                'cutOffTimeBeforeSlotValue',
                'cutOffTimeBeforeSlotType',
                'cutOffByTimeValue',
                'cutOffByTimeType',
                'cutOffByTimeNDaysPriorValue'
            ],
            isDataChanged: false
        };
    },
    async mounted() {
        this.serviceAreaId = this.$route.params.serviceAreaId;
        this.dayOfTheWeek = 0;
        this.currentTab = this.getDayOfTheWeekName(this.dayOfTheWeek);
        await this.getDeliveryWindows();
    },
    watch: {
        'deliveryWindowData.deliveryWindows': {
            deep: true,
            handler(newDeliveryWindows) {
                if (JSON.stringify(newDeliveryWindows) === this.previousDeliveryWindows) {
                    return;
                }

                const previousDeliveryWindows = JSON.parse(this.previousDeliveryWindows);
                newDeliveryWindows.forEach((item) => {
                    if (item.deliveryWindowId !== null && ![rowStatus.DELETE, rowStatus.ADD].includes(item.rowStatus)) {
                        const previousDeliveryWindow = previousDeliveryWindows.find((x) => {
                            return x.deliveryWindowId === item.deliveryWindowId;
                        });
                        const rowChanged = this.rowStateFields.some((x) => item[x] !== previousDeliveryWindow[x]);
                        if (rowChanged) {
                            this.isDataChanged = true;
                            item.rowStatus = rowStatus.EDIT;
                        }
                    }

                    if ([rowStatus.DELETE, rowStatus.ADD].includes(item.rowStatus) && !this.isDataChanged) {
                        this.isDataChanged = true;
                    }
                });

                this.previousDeliveryWindows = JSON.stringify(this.deliveryWindowData.deliveryWindows);
            }
        }
    },
    methods: {
        async handleCopyFromSelectedDay(selected) {
            if (selected !== undefined && selected !== null) {
                await this.copyDeliveryWindow(this.serviceAreaId, selected)
                    .then(this.fillDeliveryWindowFromSource)
                    .catch(this.copyDeliveryWindoError);
            }
        },
        async copyDeliveryWindow(serviceAreaId, selected) {
            this.$_handleLoaderState(true);
            const response = await this.getDeliveryWindowsData(serviceAreaId, selected);
            return response;
        },
        async copyDeliveryWindoError(error) {
            this.$_handleLoaderState(false);
            showErrorMessage(this, 'Cannot retrieve delivery windows from source.', error);
        },
        fillDeliveryWindowFromSource(response) {
            this.$_handleLoaderState(false);
            if (response?.data?.deliveryWindows && response?.data?.deliveryWindows.length !== 0) {
                if (this.deliveryWindowData?.deliveryWindows) {
                    this.deliveryWindowData.deliveryWindows = [];
                }
                this.deliveryWindowData.deliveryWindows.forEach((x) => {
                    if (x.rowStatus !== rowStatus.ADD) {
                        x.rowStatus = rowStatus.DELETE;
                    }
                });
                response.data.deliveryWindows.forEach((x) => {
                    const newDeliveryWindow = {
                        ...x,
                        deliveryWindowId: null,
                        occurrenceStartDate: null,
                        rowStatus: rowStatus.ADD
                    };

                    this.deliveryWindowData.deliveryWindows.push(newDeliveryWindow);
                });
                return;
            }

            showErrorMessage(this, 'No records to copy.');
        },
        async getDeliveryWindowsData(serviceAreaId, dayOfTheWeek) {
            const api = `/api/teams/service-areas/${serviceAreaId}/delivery-windows?dayOfTheWeek=${dayOfTheWeek}`;
            const response = await handleRequests(api);
            return response;
        },
        async getDeliveryWindows(noLoader) {
            if (!noLoader) {
                this.$_handleLoaderState(true);
            }
            try {
                const response = await this.getDeliveryWindowsData(this.serviceAreaId, this.dayOfTheWeek);
                this.deliveryWindowData = {
                    serviceAreaName: null,
                    deliveryWindows: []
                };
                await this.$nextTick(() => {
                    this.deliveryWindowData = response.data;
                    this.previousDeliveryWindows = JSON.stringify(this.deliveryWindowData.deliveryWindows);
                });
                this.copyFromDay = null;
                this.generateCopyFromDays();
                this.isDataChanged = false;
                if (!noLoader) {
                    this.$_handleLoaderState(false);
                }
            } catch (e) {
                if (!noLoader) {
                    this.$_handleLoaderState(false);
                }
                const message = 'Cannot retrieve delivery windows.';
                showErrorMessage(this, message, e);
            }
        },
        deliveryWindowDataIsValid() {
            const { deliveryWindows } = this.deliveryWindowData;
            const filteredDeliveryWindows = deliveryWindows.filter((x) => x.rowStatus !== rowStatus.DELETE);
            const missingFields = [];
            const invalidFields = [];

            if (filteredDeliveryWindows.some((x) => x.startTime == null)) {
                missingFields.push('From Time');
            }

            if (filteredDeliveryWindows.some((x) => x.endTime == null)) {
                missingFields.push('To Time');
            }

            if (
                filteredDeliveryWindows.some(
                    (x) => x.occurrence === fortnightlyOccurrence && x.occurrenceStartDate == null
                )
            ) {
                missingFields.push('Occurrence Start Date');
            }

            if (filteredDeliveryWindows.some((x) => !this.$_isValid24HourTime(x.startTime))) {
                invalidFields.push('From Time format');
            }

            if (filteredDeliveryWindows.some((x) => !this.$_isValid24HourTime(x.endTime))) {
                invalidFields.push('To Time format');
            }

            if (filteredDeliveryWindows.some((x) => !this.$_isValid24HourTimeRange(x.startTime, x.endTime))) {
                invalidFields.push('From and To Time range');
            }

            if (
                filteredDeliveryWindows.some(
                    (x) => x.occurrence === fortnightlyOccurrence && !this.$_isValidDate(x.occurrenceStartDate)
                )
            ) {
                invalidFields.push('Occurrence Start Date');
            }

            if (missingFields.length > 0) {
                showErrorMessage(
                    this,
                    `${missingFields.join(', ')} ${missingFields.length === 1 ? 'is' : 'are'} required.`
                );
                return false;
            }

            if (invalidFields.length > 0) {
                showErrorMessage(
                    this,
                    `${invalidFields.join(', ')} ${invalidFields.length === 1 ? 'is' : 'are'} invalid.`
                );
                return false;
            }

            return true;
        },
        async saveDeliveryWindows(noReload) {
            this.$_handleLoaderState(true, 'SAVING...');
            const payload = {
                method: 'post',
                data: {
                    serviceAreaId: parseInt(this.serviceAreaId, 10),
                    dayOfTheWeek: this.dayOfTheWeek,
                    deliveryWindows: this.getChangedRows()
                }
            };
            const api = '/api/teams/service-areas/delivery-windows';
            try {
                await handleRequests(api, payload);
                this.$notify({
                    message: 'Delivery windows saved!',
                    type: 'success'
                });
                if (!noReload) {
                    await this.getDeliveryWindows(true);
                }
                this.$_handleLoaderState(false);
            } catch (e) {
                this.$_handleLoaderState(false);
                const message = 'Cannot save delivery windows.';
                showErrorMessage(this, message, e);
            }
        },
        checkForUnsavedChanges(noReload) {
            return new Promise((resolve, reject) => {
                if (this.isDataChanged) {
                    this.$messageBox
                        .show({
                            class: 'sm-modal-container',
                            title: 'Save delivery window',
                            body: 'You have unsaved changes. Would you like to save the delivery window?',
                            buttons: ['Yes', 'No']
                        })
                        .then(async (response) => {
                            if (response.toLowerCase() === 'yes') {
                                await this.handleSaveDeliveryWindows(noReload);
                            }

                            resolve();
                        });
                } else {
                    resolve();
                }
            });
        },
        capitalizeFirstLetter(word) {
            return word.replace(/^\w/, (x) => x.toUpperCase());
        },
        beforeTabChanged(tab, from, next) {
            try {
                this.checkForUnsavedChanges(false).then(async () => {
                    if (next) {
                        next();
                    } else {
                        this.handleTabChanged(tab);
                    }
                });
            } catch (ex) {
                this.$notify({
                    message: ex.message,
                    type: 'danger'
                });
            }
        },
        getDayOfTheWeekName(dayOfTheWeek) {
            if (dayOfTheWeek === 0) {
                return 'sunday';
            }

            if (dayOfTheWeek === 1) {
                return 'monday';
            }

            if (dayOfTheWeek === 2) {
                return 'tuesday';
            }

            if (dayOfTheWeek === 3) {
                return 'wednesday';
            }

            if (dayOfTheWeek === 4) {
                return 'thursday';
            }

            if (dayOfTheWeek === 5) {
                return 'friday';
            }

            if (dayOfTheWeek === 6) {
                return 'saturday';
            }

            return '';
        },
        getChangedRows() {
            return this.deliveryWindowData.deliveryWindows
                .filter((x) => [rowStatus.ADD, rowStatus.EDIT, rowStatus.DELETE].includes(x.rowStatus))
                .map((x) => ({
                    ...x,
                    maxStops: x.maxStops === '' ? null : x.maxStops,
                    maxVolume: x.maxVolume === '' ? null : x.maxVolume,
                    maxWeight: x.maxWeight === '' ? null : x.maxWeight,
                    maxQuantity: x.maxQuantity === '' ? null : x.maxQuantity
                }));
        },
        generateCopyFromDays(dayOfTheWeek) {
            this.copyFrom.splice(0, this.copyFrom.length);

            const days = this.tabNames;

            for (let i = 0; i < days.length; i++) {
                if (this.dayOfTheWeek !== i) {
                    this.copyFrom.push({ id: i, name: this.capitalizeFirstLetter(days[i]) });
                }
            }
        },
        async handleSaveDeliveryWindows(noReload) {
            if (this.deliveryWindowDataIsValid()) {
                await this.saveDeliveryWindows(noReload);
            }
        },
        async handleBackToServiceAreas() {
            try {
                this.checkForUnsavedChanges(true).then(async () => {
                    this.$router.push({ name: 'Service Areas' });
                });
            } catch (ex) {
                this.$notify({
                    message: ex.message,
                    type: 'danger'
                });
            }
        },
        async handleTabChanged(tab) {
            if (tab === 'sunday') {
                this.dayOfTheWeek = 0;
            }

            if (tab === 'monday') {
                this.dayOfTheWeek = 1;
            }

            if (tab === 'tuesday') {
                this.dayOfTheWeek = 2;
            }

            if (tab === 'wednesday') {
                this.dayOfTheWeek = 3;
            }

            if (tab === 'thursday') {
                this.dayOfTheWeek = 4;
            }

            if (tab === 'friday') {
                this.dayOfTheWeek = 5;
            }

            if (tab === 'saturday') {
                this.dayOfTheWeek = 6;
            }

            await this.getDeliveryWindows();
        }
    }
};
</script>
<style lang="scss" scoped>
.card-icon {
    background-color: #384553 !important;
}
::v-deep .custom-tab-list {
    .nav-tabs {
        width: calc(100% - 5px);
        flex-wrap: wrap;
        li {
            border-bottom: 0px;
        }
    }
}

.custom-save-button,
.custom-back-button {
    width: 150px;
}

.custom-save-button {
    margin-right: 0;
}

.custom-back-button,
.custom-back-button:hover {
    background-color: #999999 !important;
    margin-left: 0;
}

.custom-delivery-window-toolbar {
    border-top: 1px solid #dddddd;
    padding-top: 10px !important;
    margin-top: 5px !important;
}

.delivery-window-page-title {
    font-weight: bold;
    font-size: 24px;
    margin-top: 20px;
    margin-bottom: 5px;
}

.delivery-window-crumb-title {
    font-weight: bold;
}

.copy-from-toolbar-container {
    margin-top: 20px;
}

.copy-from-toolbar-end-container > :first-child {
    width: 150px;
    ::v-deep .vs__dropdown-toggle {
        border-bottom: 0 !important;
    }
}
</style>
